import { type ReactNode } from "react";
import { useSession } from "../store/context";

/**
 * Full-screen transition shown while ACCEPTING an invite switches you between games: the current
 * game is left server-side (a solo game is saved + resumable; an online game hands your seat to a
 * bot) and the invited one is joined. It sits above EVERYTHING — a live table, the win banner, chat
 * — so the old game never flickers through, and the invited game's `OnlineGame` drops it the moment
 * it reaches its real phase (waiting / انتخاب یار و حریف / the table), never the lobby.
 *
 * Motion: two card glyphs — one sliding out (the game you're leaving), one sliding in (the next) —
 * plus a soft gold pulse. Disabled under `prefers-reduced-motion` (the CSS keyframes carry the
 * off-switch), leaving a clean static stage with the Persian message.
 */
export function GameSwitchOverlay(): ReactNode {
  const gameSwitch = useSession((s) => s.gameSwitch);
  if (!gameSwitch) return null;
  return (
    <div
      dir="rtl"
      role="status"
      aria-live="polite"
      className="fixed inset-0 z-[80] flex flex-col items-center justify-center gap-6 px-8 text-center"
      style={{
        background:
          "radial-gradient(ellipse at center, rgb(var(--c-surface-container) / 0.97) 0%, rgb(var(--c-surface-dim) / 0.98) 100%)",
        backdropFilter: "blur(10px)",
        animation: "bg-slidein 0.28s ease-out",
      }}
    >
      {/* Two cards: the current one leaves, the invited one arrives — the visual of the switch. */}
      <div className="relative h-24 w-40" aria-hidden="true">
        <span className="game-switch-card game-switch-card--out absolute inset-0 flex items-center justify-center rounded-xl border border-outline-variant bg-surface-container-high text-4xl shadow-lg">
          🃏
        </span>
        <span className="game-switch-card game-switch-card--in absolute inset-0 flex items-center justify-center rounded-xl border border-gold-deep/50 bg-secondary-container text-4xl shadow-xl">
          🎴
        </span>
      </div>
      <p className="m-0 max-w-[300px] font-body-lg text-body-lg font-bold leading-relaxed text-on-surface">
        {gameSwitch.message}
      </p>
      {/* A slim gold progress shimmer under the message. */}
      <span
        aria-hidden="true"
        className="game-switch-bar h-1 w-40 overflow-hidden rounded-full bg-surface-container-highest"
      />
    </div>
  );
}
